import React from 'react'
import useSWR from 'swr'
import { Skeleton } from '@mui/material'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { format } from 'date-fns'

import SideModal from '../../atoms/SideModal'
import Button from '../../atoms/Button2'
import NoteModal from '../../molecules/NoteModal'
import Note from './Note'

import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { removeTab } from '@SonarCX/slices/pinnedTabs'
import { useNoteVisibility } from '@SonarCX/providers/NoteVisibilityProvider'
import { setNoteOrder } from '@SonarCX/lib/idb'
import { moveItem } from '@SonarCX/utilities'
import useStyles from './styles'

const Notes = ({ onClose }) => {
  const dispatch = useAppDispatch()

  const { tabNotes, currentNote, setCurrentNote } = useNoteVisibility()
  const { user, loaded: envLoaded, env } = useEnvironment()

  const classes = useStyles()

  const {
    data = [],
    isLoading,
    error,
    mutate
  } = useSWR(() => {
    if (!user?.id || !envLoaded) {
      return
    }

    return `agentNotes:${env.name}:${user.id}`
  })

  const showEditor = React.useMemo(() => Boolean(currentNote), [currentNote])

  const title = React.useMemo(() => {
    if (!showEditor) return 'My notes'
    else if (currentNote?.id) return 'Edit note'
    return 'New note'
  }, [showEditor, currentNote])

  const handleOnNoteClick = React.useCallback(
    async val => {
      const inTab = tabNotes.some(tabNote => tabNote.id === val?.id)
      if (inTab) {
        dispatch(removeTab(val.id))
      }
      setCurrentNote({
        ...val
      })
    },
    [dispatch, setCurrentNote, tabNotes]
  )

  const handleModalClose = React.useCallback(() => {
    setCurrentNote(null)
  }, [setCurrentNote])

  const handleMove = React.useCallback(
    async (start: String, end: String, isAbove: boolean) => {
      if (start === end || !env || !user) {
        return
      }

      // Move the item
      const moved = moveItem(data, start, end, isAbove, note => note.id)

      // Save and upate
      await setNoteOrder({
        envName: env.name,
        userID: user.id,
        items: moved.map(note => note.id)
      })
      mutate()
    },
    [data, env, user, mutate]
  )

  React.useEffect(() => {
    if (error) dispatch(AddErrorAlert(error))
  }, [error, dispatch])

  return (
    <SideModal
      sidemodalKey='notes'
      title={title}
      onClose={onClose}
      onBack={currentNote !== null && handleModalClose}
      draggable
      minWidth={350}
      rootClass={classes.root}
    >
      {showEditor ? (
        <NoteModal onClose={handleModalClose} />
      ) : (
        <>
          <Button
            onClick={() =>
              setCurrentNote({
                title: format(new Date(), 'MMM d yyyy, h:mm aaa')
              } as APIAgentNote)
            }
            className={classes.newNote}
          >
            + New note
          </Button>

          {isLoading ? (
            <>
              <div className={classes.noteRow}>
                <Skeleton variant='text' width={'100%'} />
              </div>

              <div className={classes.noteRow}>
                <Skeleton variant='text' width={'100%'} />
              </div>
            </>
          ) : (
            data.map((note: APIAgentNote, i) => (
              <Note
                key={note.id}
                note={note}
                onClick={() => handleOnNoteClick(note)}
                onMove={handleMove}
                isFirst={i === 0}
                isLast={i === data.length - 1}
              />
            ))
          )}
        </>
      )}
    </SideModal>
  )
}

export default Notes
